import type { DataBuilderCompanySet } from '../data-builder.store';

type SummarySelectionItem = {
  label: string;
};

type SelectionSummaryInput =
  | { type: 'unit_of_analysis'; selections: SummarySelectionItem[] }
  | { type: 'company_selections'; count: number }
  | { type: 'common_set'; companySets: DataBuilderCompanySet[] }
  | { type: 'company_mapper' }
  | { type: 'company_mapper_with_selections'; count: number }
  | { type: 'none' };

const MAX_LIST_ITEMS = 3;

const withEllipsis = (items: string[]) => {
  if (items.length <= MAX_LIST_ITEMS) {
    return items.join(', ');
  }
  return `${items.slice(0, MAX_LIST_ITEMS).join(', ')}, ...`;
};

export const formatCompanySetName = (
  companySet: DataBuilderCompanySet
): string => {
  if (companySet === 'public_us') return 'US Public Companies';
  if (companySet === 'revelio_shared') return 'Revelio Standard';
  if (companySet === 'revelio_shared_private') {
    return 'Revelio Standard Private';
  }
  if (companySet === 'revelio_shared_public') return 'Revelio Standard Public';

  return companySet
    .split('_')
    .map((part) => part.toUpperCase())
    .join(' ');
};

export const formatSelectionSummary = (
  input: SelectionSummaryInput
): string => {
  if (input.type === 'none') {
    return '';
  }

  if (input.type === 'company_mapper') {
    return 'Selected from List';
  }

  if (input.type === 'company_mapper_with_selections') {
    if (input.count === 0) {
      return '';
    }
    const suffix = input.count === 1 ? 'company' : 'companies';
    return `${input.count} ${suffix} selected`;
  }

  if (input.type === 'company_selections') {
    if (input.count === 0) {
      return '';
    }
    const suffix = input.count === 1 ? 'company' : 'companies';
    return `${input.count} ${suffix} selected`;
  }

  if (input.type === 'common_set') {
    if (input.companySets.length === 0) {
      return '';
    }
    return withEllipsis(input.companySets.map(formatCompanySetName));
  }

  if (input.selections.length === 0) {
    return '';
  }
  return withEllipsis(input.selections.map((selection) => selection.label));
};
